import type { PageViewport, PDFPageProxy } from 'pdfjs-dist';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PdfPageStack, SCALE, usePdfDocumentPages } from './pdfPageRender';

/** One AcroForm widget occurrence with its on-canvas (CSS px) geometry. */
type Widget = {
  key: string;
  name: string;
  fieldType: string;
  /**
   * The "on" state of a checkbox/radio widget, when it has one.
   *
   * pdf.js reports this under DIFFERENT properties for the two button kinds: a
   * checkbox carries `exportValue`, a radio button carries `buttonValue` and
   * leaves `exportValue` undefined. Reading only one of them silently loses
   * every radio group — see {@link parseWidgets}.
   */
  onState?: string;
  /**
   * The string a mapping stores/compares against. A button widget carrying an
   * on-state keys as `name:onState`, so a mapping can address the individual
   * widget of a group that shares one AcroForm field name (BMO Title / Gender /
   * frequency rows) — matching fillPdf.ts's `resolveFillTarget`. Every other
   * widget keys by name alone.
   */
  mappingKey: string;
  pageIndex: number;
  left: number;
  top: number;
  width: number;
  height: number;
};

type Props = {
  templateId: string;
  /** S3 object key of the PDF to render (EN or FR variant). */
  fileKey: string;
  /** Lower-cased search term; dims non-matching widget names. */
  search: string;
  /** The active form field's AcroForm names in the selected PDF — their widgets
   *  render as "selected". */
  activeNames: Set<string>;
  /**
   * AcroField name → breadcrumb labels of the OTHER form fields already mapped to
   * it, in workflow (waterfall fill) order. Mapping is many-to-many, so a box can
   * legitimately carry several — clicking it adds the active field alongside them
   * rather than taking it over.
   */
  assignedNames: Map<string, string[]>;
  /** No active field selected, or the workflow is read-only → clicks are inert. */
  disabled: boolean;
  onPick: (fieldName: string) => void;
  /**
   * Every AcroForm name this document actually offers, reported once per parse.
   * The page uses it to flag a mapping that points at a name the PDF does not
   * have — the failure that is otherwise completely silent, and the one that
   * let a French template ship with every box empty while the tool showed a row
   * of green "mapped" ticks.
   */
  onNamesParsed?: (names: string[]) => void;
  /**
   * Current zoom factor. Owned by the page so the control can live in the
   * toolbar: a control inside this scrolling area slid out of sight the moment
   * the user panned a magnified page sideways (`position: sticky` pins
   * vertically only).
   */
  zoom: number;
};

// Rasterization scale, zoom steps and the page/canvas stack now live in
// `pdfPageRender`, shared with the e-signature placement editor. Re-exported
// here because the field-mapping page imports them from this module.
export { DEFAULT_ZOOM_INDEX, ZOOM_STEPS } from './pdfPageRender';

/**
 * Renders every page of an uploaded PDF via pdf.js and overlays a clickable box
 * on each AcroForm widget. The rasterization itself is `PdfPageStack`'s job;
 * what is specific to this viewer is the widget geometry and the mapped /
 * assigned / searched styling of each box.
 */
export function PdfAcroViewer({
  templateId,
  fileKey,
  search,
  activeNames,
  assignedNames,
  disabled,
  onPick,
  onNamesParsed,
  zoom,
}: Props) {
  const { t } = useTranslation();
  const [widgets, setWidgets] = useState<Widget[]>([]);

  // Widget geometry is harvested in the SAME parse pass that reads the page
  // dimensions — one walk of the document, not two.
  const { pages, pageProxiesRef, loading, error } = usePdfDocumentPages(
    templateId,
    fileKey,
    async (proxies) => {
      const found = await parseWidgets(proxies);
      setWidgets(found);
      // Both the bare name and the `name:onState` key: a mapping may be stored
      // either way, and comparing against only one shape would flag correct
      // per-option mappings as missing.
      onNamesParsed?.([...new Set(found.flatMap((w) => [w.name, w.mappingKey]))]);
    },
  );

  // Qualified-key indexes, rebuilt only when the underlying key sets change —
  // the widget lookup runs once per widget per render (229 on the RESP form).
  const assignedQualified = useMemo(
    () => indexQualifiedKeys(assignedNames.keys()),
    [assignedNames],
  );
  const activeQualified = useMemo(() => indexQualifiedKeys(activeNames), [activeNames]);

  const term = search.trim().toLowerCase();
  const widgetsByPage = useMemo(() => {
    const map = new Map<number, Widget[]>();
    for (const w of widgets) {
      const list = map.get(w.pageIndex) ?? [];
      list.push(w);
      map.set(w.pageIndex, list);
    }
    return map;
  }, [widgets]);

  if (error) {
    return (
      <div style={{ padding: 24, color: '#b91c1c', fontSize: 13 }}>
        {t('pdfMapping.loadPdfFailed')}
      </div>
    );
  }
  if (loading) {
    return (
      <div style={{ padding: 24, color: '#6b7280', fontSize: 13 }}>
        {t('pdfMapping.loadingPdf')}
      </div>
    );
  }
  if (widgets.length === 0) {
    return (
      <div style={{ padding: 24, color: '#6b7280', fontSize: 13 }}>
        {t('pdfMapping.noAcroFields')}
      </div>
    );
  }

  return (
    <PdfPageStack
      pages={pages}
      zoom={zoom}
      pageProxiesRef={pageProxiesRef}
      renderOverlay={(pageIndex) =>
        (widgetsByPage.get(pageIndex) ?? []).map((w) => {
          const isActive =
            widgetAssignedKeys(w.name, w.onState, activeNames, activeQualified).length > 0;
          const assignedTo = widgetAssignedKeys(
            w.name,
            w.onState,
            assignedNames,
            assignedQualified,
          ).flatMap((k) => assignedNames.get(k) ?? []);
          const isAssigned = !isActive && assignedTo.length > 0;
          // How many form fields fill this box in total — the active one counts
          // too, since its mapping is excluded from `assignedNames`. Anything
          // above 1 is a shared box that falls back in workflow order.
          const claimants = assignedTo.length + (isActive ? 1 : 0);
          const dimmed = term !== '' && !w.mappingKey.toLowerCase().includes(term);
          const { border, background } = boxStyle(isActive, isAssigned);
          return (
            <button
              type="button"
              key={w.key}
              title={
                assignedTo.length > 0
                  ? t('pdfMapping.acroAssignedTo', {
                      name: w.mappingKey,
                      fields: assignedTo.join(' · '),
                    })
                  : w.mappingKey
              }
              disabled={disabled}
              onClick={() => onPick(w.mappingKey)}
              style={{
                position: 'absolute',
                // Widget geometry is captured at base SCALE, so it is scaled
                // by the same factor the canvas was rasterized with.
                left: w.left * zoom,
                top: w.top * zoom,
                width: w.width * zoom,
                height: w.height * zoom,
                padding: 0,
                margin: 0,
                border,
                background,
                borderRadius: 2,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: dimmed ? 0.25 : 1,
                transition: 'background 0.1s, border-color 0.1s',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: -16,
                  left: 0,
                  fontSize: 9,
                  lineHeight: '14px',
                  padding: '0 3px',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                  color: '#fff',
                  background: isActive ? '#0179C1' : isAssigned ? '#059669' : '#6b7280',
                  pointerEvents: 'none',
                  maxWidth: 220,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {claimants > 1 ? `${w.mappingKey} ×${claimants}` : w.mappingKey}
              </span>
            </button>
          );
        })
      }
    />
  );
}

/**
 * Every AcroForm widget in the document, with its geometry in CSS px at base
 * `SCALE`.
 *
 * `convertToViewportRectangle` is what flips pdf.js's bottom-left PDF-point
 * origin into top-left CSS px; the min / abs normalisation is needed because a
 * PDF `/Rect`'s corners are unordered.
 */
export async function parseWidgets(proxies: PDFPageProxy[]): Promise<Widget[]> {
  const found: Widget[] = [];
  for (const [index, page] of proxies.entries()) {
    const viewport: PageViewport = page.getViewport({ scale: SCALE });
    const annots = await page.getAnnotations();
    for (const a of annots) {
      // Widget annotations that carry a field name are the AcroForm fields.
      // Push/radio/checkbox groups produce multiple widgets sharing one
      // name — we draw every box but assign by name.
      if (a.subtype !== 'Widget' || !a.fieldName) continue;
      const rect = viewport.convertToViewportRectangle(a.rect) as number[];
      const x1 = rect[0] ?? 0;
      const y1 = rect[1] ?? 0;
      const x2 = rect[2] ?? 0;
      const y2 = rect[3] ?? 0;
      // The widget's "on" state. pdf.js splits this across TWO properties: a
      // CHECKBOX gets `exportValue`, a RADIO button gets `buttonValue` and
      // leaves `exportValue` undefined (see AnnotationFactory's radioButton
      // branch). Reading only `exportValue` — as this did — keyed every radio
      // widget by its bare field name, so the four Title boxes all resolved to
      // `Radio#1`: correctly mapped boxes rendered as unmapped, and clicking one
      // stored a handle on the whole group instead of the option the originator
      // clicked. The TS annotation type is loose, so read both defensively.
      const btn = a as { exportValue?: string; buttonValue?: string };
      const onState = btn.buttonValue || btn.exportValue || undefined;
      found.push({
        key: `${index + 1}:${a.id}`,
        name: a.fieldName,
        fieldType: a.fieldType ?? '',
        onState,
        mappingKey: a.fieldName, // finalized in the post-pass below
        pageIndex: index,
        left: Math.min(x1, x2),
        top: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      });
    }
  }

  // Finalize each widget's mapping key: any button widget with an on-state keys
  // as `name:onState`, so clicking it stores a handle on THAT option rather than
  // on the group. Deliberately not conditional on the name being shared by
  // several widgets: a lone radio still needs its state (pdf-lib selects a radio
  // group by option name, and a bare handle would hand it `true`), and a lone
  // checkbox authored as `name:Yes` has to resolve too. Bare mappings keep
  // working because the lookup accepts either shape — see {@link widgetLookupKeys}.
  for (const w of found) {
    w.mappingKey = w.onState ? `${w.name}:${w.onState}` : w.name;
  }
  return found;
}

/**
 * Group the authored mapping keys by the AcroForm field name they address, for
 * the qualified (`name:value`) ones only. Built once per render so the widget
 * lookup below stays cheap on a 229-widget document.
 */
export function indexQualifiedKeys(keys: Iterable<string>): Map<string, string[]> {
  const byName = new Map<string, string[]>();
  for (const k of keys) {
    const colon = k.indexOf(':');
    if (colon <= 0) continue;
    const bare = k.slice(0, colon);
    const list = byName.get(bare);
    if (list) list.push(k);
    else byName.set(bare, [k]);
  }
  return byName;
}

/**
 * Which authored mapping keys actually address this widget.
 *
 * A mapping is stored either bare (`Combo Box7`) or qualified with a value
 * (`Combo Box7:Alberta`), and `resolveFillTarget` accepts both — it tries the
 * literal name, then falls back to the part before the colon. The viewer has to
 * mirror that or a box which fills perfectly well renders grey. The two widget
 * kinds resolve differently, and conflating them is what produced two separate
 * bugs:
 *
 *  - A BUTTON widget (checkbox / radio) has an on-state, and each of a group's
 *    widgets is a distinct box. It matches its OWN `name:onState` and a bare
 *    `name` handle (which fills the whole group), but never a sibling's state —
 *    otherwise picking "Mr" would light all four Title boxes.
 *  - Every other widget (text, DROPDOWN, signature) has no on-state and is the
 *    only box under its name. A dropdown's per-option handles — `Combo Box7:
 *    Alberta` and its twelve siblings — all fill this one widget, so they all
 *    count. Matching only the bare name left every province mapping looking
 *    unmapped.
 */
export function widgetAssignedKeys(
  name: string,
  onState: string | undefined,
  authored: { has(key: string): boolean },
  qualifiedByName: ReadonlyMap<string, readonly string[]>,
): string[] {
  if (onState) {
    return [`${name}:${onState}`, name].filter((k) => authored.has(k));
  }
  return [name, ...(qualifiedByName.get(name) ?? [])].filter((k) => authored.has(k));
}

function boxStyle(isActive: boolean, isAssigned: boolean): { border: string; background: string } {
  if (isActive) return { border: '2px solid #0179C1', background: 'rgba(1,121,193,0.22)' };
  if (isAssigned) return { border: '1.5px solid #059669', background: 'rgba(5,150,105,0.14)' };
  return { border: '1.5px solid #6b7280', background: 'rgba(156,163,175,0.14)' };
}
